// How a booking names itself on the board.
//
// One function, because there are six views and they must not disagree about
// what a maintenance booking is called. Before migration 013 every view could
// safely write `${jobNumber} — ${jobTitle}`; a maintenance booking has neither,
// and the failure mode of leaving that alone is a bar reading "null — null",
// which is the kind of thing that ships.
//
// The wording is deliberately the same in all of them: a person scanning the
// board should not have to work out that "Maintenance" here and "Service"
// there are the same thing.
import type { CalendarBooking } from '../schedulingApi';

// Only what the label is actually built from. `resourceName` used to be in
// here and was never read, which shut this function out of the one caller that
// most needed it: the re-planning notice, whose rows carry a job and a supplier
// but no resource name — it already knows which machine it is talking about.
type Labelled = Pick<CalendarBooking, 'kind' | 'jobNumber' | 'jobTitle' | 'supplierName'>;

/** The full form — a re-planning row, a tooltip, a panel heading. */
export function bookingLabel(booking: Labelled): string {
  if (booking.kind === 'maintenance') {
    // Naming the supplier is the whole point: "Maintenance" says the machine
    // is away, and only the supplier says who to ring about it.
    return booking.supplierName ? `Maintenance · ${booking.supplierName}` : 'Maintenance';
  }
  const number = booking.jobNumber ?? '';
  const title = booking.jobTitle ?? '';
  return title ? `${number} · ${title}` : number;
}
